import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Button } from 'atoms';
import { WhiteboardErrorCode, useCreateWhiteboard } from 'core';

import { ContinueBoardModal } from './ContinueBoardModal';

interface IWhiteboardStartPanel {
  lessonId: string;
  groupId: string;
  /** Re-reads the lesson board once one was created/linked → editor opens. */
  onBoardReady: () => Promise<unknown>;
}

/** Teacher-only choice for a lesson without a board: new board / continue an existing one. */
export const WhiteboardStartPanel = memo(
  ({ lessonId, groupId, onBoardReady }: IWhiteboardStartPanel) => {
    const { t } = useTranslation();
    const [isContinueOpen, setContinueOpen] = useState(false);

    const { createWhiteboard, loading: creating } = useCreateWhiteboard();

    const handleCreate = useCallback(async () => {
      try {
        const result = await createWhiteboard(lessonId);
        if (result.success) {
          await onBoardReady();
          return;
        }
        if (result.errorCode === WhiteboardErrorCode.LESSON_ALREADY_HAS_BOARD) {
          // Raced with another tab — the board exists now, just load it.
          await onBoardReady();
          return;
        }
        message.error(t('whiteboard.errors.createFailed'));
      } catch {
        message.error(t('whiteboard.errors.createFailed'));
      }
    }, [createWhiteboard, lessonId, onBoardReady, t]);

    return (
      <PanelWrapper>
        <PanelTitle>{t('whiteboard.startPanel.title')}</PanelTitle>
        <PanelActions>
          <ActionButton variant="primary" isLoading={creating} onClick={handleCreate}>
            {t('whiteboard.startPanel.new')}
          </ActionButton>
          <ActionButton variant="outline" onClick={() => setContinueOpen(true)}>
            {t('whiteboard.startPanel.continue')}
          </ActionButton>
        </PanelActions>

        <ContinueBoardModal
          open={isContinueOpen}
          lessonId={lessonId}
          groupId={groupId}
          onClose={() => setContinueOpen(false)}
          onLinked={onBoardReady}
        />
      </PanelWrapper>
    );
  },
);

const PanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`;

const PanelTitle = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
`;

const PanelActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  ${tablet} {
    flex-direction: row;
    width: auto;
  }
`;

const ActionButton = styled(Button)`
  ${tablet} {
    width: 260px;
  }
`;
